/**
 * Modal "+ Ingresar stock" — Sprint 17 S17-13.
 *
 * Form variable por tipo:
 * - llavero: cantidad + tabla expandible con N filas (identificador + serial_rfid por unidad).
 * - control_remoto, pila, otro: solo cantidad. Backend inserta N filas idénticas.
 *
 * El backend (POST /stock-recursos/ingresar) valida:
 * - tipo='llavero' exige len(items) === cantidad y sin duplicados.
 * - tipos no-llavero NO admiten items.
 * - costo_compra y valor_venta deben ser >= 0; cantidad > 0.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { ingresarStock } from '../../lib/api/stock-recursos';
import type {
  StockRecursoIngresar,
  StockRecursoItemLlavero,
  TipoStock,
} from '../../lib/api/types';
import { Button } from '../ui/Button';
import { InputMontoCLP } from '../ui/InputMontoCLP';
import { Num } from './Monto';
import { parsearCSV, type ResultadoParseo } from '../../lib/csv-llaveros';

// Sprint 17 Z11 — modo de carga del listado de llaveros.
type ModoLlavero = 'manual' | 'csv';

interface Props {
  onCerrar: () => void;
  onIngresado: (cantidad: number, tipo: TipoStock) => void;
}

const TIPOS: { value: TipoStock; label: string }[] = [
  { value: 'llavero', label: 'Llavero' },
  { value: 'control_remoto', label: 'Control remoto' },
  { value: 'pila', label: 'Pila' },
  { value: 'otro', label: 'Otro' },
];

function nuevoItem(): StockRecursoItemLlavero {
  return { identificador: '', serial_rfid: '' };
}

export function ModalIngresarStock({ onCerrar, onIngresado }: Props) {
  const [tipo, setTipo] = useState<TipoStock>('llavero');
  // Sprint 17 S17-Z13: state numérico con InputMontoCLP.
  const [costoCompra, setCostoCompra] = useState<number | null>(null);
  const [valorVenta, setValorVenta] = useState<number | null>(null);
  const [cantidad, setCantidad] = useState('1');
  const [items, setItems] = useState<StockRecursoItemLlavero[]>([nuevoItem()]);
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sprint 17 Z11 — modo CSV alternativo para llaveros.
  const [modoLlavero, setModoLlavero] = useState<ModoLlavero>('manual');
  const [csvTexto, setCsvTexto] = useState('');
  const resultadoCSV = useMemo<ResultadoParseo | null>(() => {
    if (modoLlavero !== 'csv' || tipo !== 'llavero' || !csvTexto.trim()) {
      return null;
    }
    return parsearCSV(csvTexto);
  }, [modoLlavero, tipo, csvTexto]);

  // En modo CSV, la cantidad la determina el resultado parseado.
  const cantidadEfectiva =
    tipo === 'llavero' && modoLlavero === 'csv'
      ? (resultadoCSV?.filas.length ?? 0)
      : Number.parseInt(cantidad || '0', 10);

  // Sincroniza el largo de items con cantidad cuando tipo=llavero
  // (solo en modo manual — el modo CSV genera items desde resultadoCSV).
  useEffect(() => {
    if (tipo !== 'llavero' || modoLlavero !== 'manual') return;
    const n = Number.parseInt(cantidad || '0', 10);
    setItems((prev) => {
      if (n <= 0) return [];
      if (prev.length === n) return prev;
      if (n > prev.length) {
        return [
          ...prev,
          ...Array.from({ length: n - prev.length }, () => nuevoItem()),
        ];
      }
      return prev.slice(0, n);
    });
  }, [tipo, modoLlavero, cantidad]);

  const costoNum = costoCompra ?? 0;
  const valorNum = valorVenta ?? 0;

  // Items efectivos según modo (manual = state items; csv = filas parseadas).
  const itemsEfectivos: StockRecursoItemLlavero[] =
    tipo === 'llavero' && modoLlavero === 'csv'
      ? (resultadoCSV?.filas.map((f) => ({
          identificador: f.identificador,
          serial_rfid: f.serial_rfid,
        })) ?? [])
      : items;

  const csvSinErrores =
    modoLlavero !== 'csv' ||
    (resultadoCSV !== null &&
      resultadoCSV.errores.length === 0 &&
      resultadoCSV.filas.length > 0 &&
      resultadoCSV.identificadores_duplicados.size === 0 &&
      resultadoCSV.series_duplicadas.size === 0);

  const formValido =
    cantidadEfectiva > 0 &&
    costoNum >= 0 &&
    valorNum >= 0 &&
    (tipo !== 'llavero' ||
      (csvSinErrores &&
        itemsEfectivos.every(
          (it) => it.identificador.trim() !== '' && it.serial_rfid.trim() !== '',
        )));

  function actualizarItem(
    idx: number,
    campo: 'identificador' | 'serial_rfid',
    valor: string,
  ) {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)),
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: StockRecursoIngresar = {
        tipo,
        costo_compra: costoNum,
        valor_venta: valorNum,
        cantidad: cantidadEfectiva,
        notas: notas.trim() || null,
        items:
          tipo === 'llavero'
            ? itemsEfectivos.map((it) => ({
                identificador: it.identificador.trim(),
                serial_rfid: it.serial_rfid.trim(),
              }))
            : null,
      };
      const creados = await ingresarStock(payload);
      onIngresado(creados.length, tipo);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Ingresar stock"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Ingresar stock
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm text-xs text-text-secondary">
          Cada compra se registra como un lote: tipo + cantidad + costo +
          valor de venta. Para llaveros, cada unidad necesita su
          identificador del barrio (LL-NNNN) y el número de serie del
          proveedor. Los demás tipos van por cantidad (los identificadores
          se asignan en otro momento).
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Tipo</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoStock)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Cantidad
              {tipo === 'llavero' && modoLlavero === 'csv' && (
                <span className="text-text-muted text-[10px] ml-1">
                  (auto desde CSV)
                </span>
              )}
            </span>
            <input
              type="number"
              min={1}
              value={
                tipo === 'llavero' && modoLlavero === 'csv'
                  ? String(cantidadEfectiva)
                  : cantidad
              }
              onChange={(e) => setCantidad(e.target.value)}
              disabled={tipo === 'llavero' && modoLlavero === 'csv'}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary disabled:opacity-60"
              required
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Costo compra (CLP, total de la compra o unitario)</span>
            <InputMontoCLP
              value={costoCompra}
              onChange={setCostoCompra}
              placeholder="0"
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            />
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Valor de venta (CLP por unidad)</span>
            <InputMontoCLP
              value={valorVenta}
              onChange={setValorVenta}
              placeholder="0"
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            />
          </label>
        </div>

        {tipo === 'llavero' && (
          <div className="space-y-2">
            {/* Sprint 17 Z11 — toggle Manual / CSV */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-text-secondary">Modo de carga:</span>
              {(['manual', 'csv'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModoLlavero(m)}
                  className={`px-2.5 py-1 rounded border ${
                    modoLlavero === m
                      ? 'border-orange text-orange bg-orange/10'
                      : 'border-white/[0.08] text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {m === 'manual' ? 'Manual' : 'CSV'}
                </button>
              ))}
            </div>

            {modoLlavero === 'csv' ? (
              <CSVLlaveroSection
                texto={csvTexto}
                onTextoChange={setCsvTexto}
                resultado={resultadoCSV}
              />
            ) : (
              <ManualLlaveroSection
                items={items}
                onActualizar={actualizarItem}
              />
            )}
          </div>
        )}


        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            placeholder="Ej: Compra Sodimac 2026-05-25, factura 12345"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!formValido || submitting}
          >
            {submitting ? 'Ingresando…' : 'Ingresar al stock'}
          </Button>
        </div>
      </form>
    </div>
  );
}


// ===== Sprint 17 Z11 — Sub-componentes auxiliares =====

function ManualLlaveroSection({
  items,
  onActualizar,
}: {
  items: StockRecursoItemLlavero[];
  onActualizar: (
    idx: number,
    campo: 'identificador' | 'serial_rfid',
    valor: string,
  ) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm text-text-secondary">
        Items ({items.length})
      </div>
      <div className="border-[0.5px] border-white/[0.08] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-sunken text-xs uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium w-12">#</th>
              <th className="text-left px-2 py-1.5 font-medium">
                Identificador (LL-NNNN)
              </th>
              <th className="text-left px-2 py-1.5 font-medium">
                Número de serie
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr
                key={idx}
                className="border-t-[0.5px] border-white/[0.06]"
              >
                <td className="px-2 py-1 text-text-muted">
                  <Num>{idx + 1}</Num>
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={it.identificador}
                    onChange={(e) =>
                      onActualizar(idx, 'identificador', e.target.value)
                    }
                    placeholder="LL-0001"
                    aria-label={`Identificador llavero ${idx + 1}`}
                    className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1 text-text-primary font-mono"
                    required
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={it.serial_rfid}
                    onChange={(e) =>
                      onActualizar(idx, 'serial_rfid', e.target.value)
                    }
                    placeholder="1234567890"
                    aria-label={`Número de serie llavero ${idx + 1}`}
                    className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1 text-text-primary font-mono"
                    required
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function CSVLlaveroSection({
  texto,
  onTextoChange,
  resultado,
}: {
  texto: string;
  onTextoChange: (t: string) => void;
  resultado: ResultadoParseo | null;
}) {
  function handleArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const contenido = reader.result;
      if (typeof contenido === 'string') {
        onTextoChange(contenido);
      }
    };
    reader.readAsText(file);
  }

  const hayErrores =
    resultado !== null &&
    (resultado.errores.length > 0 ||
      resultado.identificadores_duplicados.size > 0 ||
      resultado.series_duplicadas.size > 0);

  return (
    <div className="space-y-3">
      <div className="text-xs text-text-muted">
        Formato esperado: encabezado{' '}
        <code className="font-mono">identificador,serial_rfid</code> (o{' '}
        <code className="font-mono">;</code> como separador, Excel CL).
        Una fila por llavero. Se detecta el separador automáticamente.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="csv-file-input"
          className="text-xs cursor-pointer px-3 py-1.5 rounded border border-white/[0.12] text-text-secondary hover:text-text-primary hover:border-white/[0.24] inline-block"
        >
          Subir archivo CSV
          <input
            id="csv-file-input"
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={handleArchivo}
            className="hidden"
          />
        </label>
        {texto && (
          <button
            type="button"
            onClick={() => onTextoChange('')}
            className="text-xs text-text-muted hover:text-state-danger underline-offset-2 hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>

      <textarea
        value={texto}
        onChange={(e) => onTextoChange(e.target.value)}
        rows={5}
        placeholder={
          'identificador,serial_rfid\nLL-0001,1234567890\nLL-0002,1234567891'
        }
        className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary font-mono text-xs"
        aria-label="Contenido CSV"
      />

      {resultado && (
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-3">
            <span className="text-text-secondary">
              Filas detectadas:{' '}
              <strong className="text-text-primary">
                {resultado.filas.length}
              </strong>
            </span>
            <span className="text-text-muted">
              Separador:{' '}
              <code className="font-mono">
                {resultado.separador === ';' ? ';' : ','}
              </code>
            </span>
          </div>

          {resultado.errores.length > 0 && (
            <ul className="bg-state-danger/[0.08] border-l-4 border-state-danger p-2 rounded-sm space-y-0.5">
              {resultado.errores.map((e, i) => (
                <li key={i} className="text-text-secondary">
                  {e}
                </li>
              ))}
            </ul>
          )}

          {resultado.identificadores_duplicados.size > 0 && (
            <div className="bg-state-warning/[0.08] border-l-4 border-state-warning p-2 rounded-sm text-text-secondary">
              Identificadores duplicados:{' '}
              <Num>
                {Array.from(resultado.identificadores_duplicados).join(', ')}
              </Num>
            </div>
          )}
          {resultado.series_duplicadas.size > 0 && (
            <div className="bg-state-warning/[0.08] border-l-4 border-state-warning p-2 rounded-sm text-text-secondary">
              Números de serie duplicados:{' '}
              <Num>
                {Array.from(resultado.series_duplicadas).join(', ')}
              </Num>
            </div>
          )}

          {resultado.filas.length > 0 && !hayErrores && (
            <div className="border-[0.5px] border-white/[0.08] rounded overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-bg-sunken text-text-secondary sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium w-12">#</th>
                    <th className="text-left px-2 py-1 font-medium">
                      Identificador
                    </th>
                    <th className="text-left px-2 py-1 font-medium">
                      N° serie
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.filas.map((f, i) => (
                    <tr
                      key={i}
                      className="border-t-[0.5px] border-white/[0.04]"
                    >
                      <td className="px-2 py-0.5 text-text-muted">
                        <Num>{i + 1}</Num>
                      </td>
                      <td className="px-2 py-0.5 text-text-primary">
                        <Num>{f.identificador}</Num>
                      </td>
                      <td className="px-2 py-0.5 text-text-secondary">
                        <Num>{f.serial_rfid}</Num>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
